import {
  UserAgent,
  Registerer,
  RegistererState,
  Inviter,
  Invitation,
  Session,
  SessionState,
} from 'sip.js';
import type { UserAgentOptions } from 'sip.js/lib/api/user-agent-options';

export interface SIPConfig {
  server: string;
  username: string;
  password: string;
  displayName?: string;
}

export interface CallSession {
  id: string;
  session: Session;
  direction: 'inbound' | 'outbound';
  remoteIdentity: string;
  state: SessionState;
  localStream?: MediaStream;
  remoteStream?: MediaStream;
}

export type SIPEventType =
  | 'registered'
  | 'unregistered'
  | 'registrationFailed'
  | 'incomingCall'
  | 'callEstablished'
  | 'callEnded'
  | 'callFailed';

export type SIPEventHandler = (data?: any) => void;

class SIPClient {
  private userAgent: UserAgent | null = null;
  private registerer: Registerer | null = null;
  private sessions: Map<string, CallSession> = new Map();
  private eventHandlers: Map<SIPEventType, Set<SIPEventHandler>> = new Map();
  private audioElement: HTMLAudioElement | null = null;
  private isConnecting: boolean = false;
  private isDisconnecting: boolean = false;

  // Ring tone state
  private audioContext: AudioContext | null = null;
  private ringToneInterval: ReturnType<typeof setInterval> | null = null;

  constructor() {
    this.audioElement = document.createElement('audio');
    this.audioElement.autoplay = true;
    // Attach to DOM so autoplay works and audio context is usable
    this.audioElement.style.cssText = 'position:absolute;width:0;height:0;opacity:0;pointer-events:none;';
    document.body.appendChild(this.audioElement);

    // Pre-unlock the AudioContext on the first user gesture so it is already
    // in 'running' state when an inbound call arrives (before the user clicks Answer).
    const unlock = () => {
      this.unlockAudio();
      document.removeEventListener('click', unlock, true);
      document.removeEventListener('keydown', unlock, true);
    };
    document.addEventListener('click', unlock, { capture: true, once: true });
    document.addEventListener('keydown', unlock, { capture: true, once: true });
  }

  /**
   * Call this on any user gesture to unlock the Web Audio API context.
   * The HubSpot widget / test cradle should call this on button clicks.
   */
  unlockAudio(): void {
    if (!this.audioContext) {
      try {
        this.audioContext = new AudioContext();
      } catch (e) {
        console.warn('SIP: Could not create AudioContext', e);
        return;
      }
    }
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume().catch(() => {});
    }
  }

  private startRingTone(): void {
    this.stopRingTone();
    if (!this.audioContext) {
      try {
        this.audioContext = new AudioContext();
      } catch (e) {
        return;
      }
    }
    const ctx = this.audioContext;

    const playBeep = () => {
      if (ctx.state !== 'running') return;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.setValueAtTime(480, ctx.currentTime);
      gain.gain.setValueAtTime(0.25, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.4);
    };

    const startBeeping = () => {
      playBeep();
      setTimeout(playBeep, 350);
      this.ringToneInterval = setInterval(() => {
        playBeep();
        setTimeout(playBeep, 350);
      }, 1500);
    };

    if (ctx.state === 'suspended') {
      // Await resume before playing — this is the key fix
      ctx.resume().then(startBeeping).catch(() => {});
    } else {
      startBeeping();
    }
  }

  private stopRingTone(): void {
    if (this.ringToneInterval !== null) {
      clearInterval(this.ringToneInterval);
      this.ringToneInterval = null;
    }
  }

  async connect(config: SIPConfig): Promise<void> {
    // Prevent multiple simultaneous connections
    if (this.isConnecting) {
      console.log('SIP: Already connecting, skipping...');
      return;
    }

    // If already connected, skip
    if (this.userAgent) {
      console.log('SIP: Already connected');
      return;
    }

    this.isConnecting = true;

    try {
      // Extract hostname from WebSocket server URL (strip wss://, port, and path)
      const serverHost = config.server
        .replace(/^(wss?:\/\/)/i, '')
        .replace(/:\d+.*$/, '')
        .replace(/\/.*$/, '');

      // SIP URI uses hostname only (not the WebSocket port)
      const uri = `sip:${config.username}@${serverHost}`;
      console.log(`SIP: Connecting as ${uri} via ${config.server}`);

      const options: UserAgentOptions = {
        uri: UserAgent.makeURI(uri)!,
        authorizationUsername: config.username,
        authorizationPassword: config.password,
        displayName: config.displayName || config.username,
        transportOptions: {
          server: config.server,
        },
        sessionDescriptionHandlerFactoryOptions: {
          constraints: {
            audio: true,
            video: false,
          },
        },
      };

      this.userAgent = new UserAgent(options);

      this.userAgent.delegate = {
        onInvite: (invitation: Invitation) => {
          this.handleIncomingCall(invitation);
        },
      };

      await this.userAgent.start();

      this.registerer = new Registerer(this.userAgent);

      this.registerer.stateChange.addListener((state: RegistererState) => {
        switch (state) {
          case RegistererState.Registered:
            this.emit('registered');
            break;
          case RegistererState.Unregistered:
            this.emit('unregistered');
            break;
        }
      });

      await this.registerer.register();
    } catch (error) {
      console.error('SIP connection/registration failed:', error);
      this.emit('registrationFailed', error);
      // Clean up on failure
      this.userAgent = null;
      this.registerer = null;
      throw error;
    } finally {
      this.isConnecting = false;
    }
  }

  async disconnect(): Promise<void> {
    // Prevent multiple simultaneous disconnects
    if (this.isDisconnecting) {
      console.log('SIP: Already disconnecting, skipping...');
      return;
    }

    // If not connected, skip
    if (!this.userAgent) {
      console.log('SIP: Not connected, nothing to disconnect');
      return;
    }

    this.isDisconnecting = true;

    try {
      this.sessions.forEach((callSession) => {
        this.hangup(callSession.id);
      });

      if (this.registerer) {
        try {
          await this.registerer.unregister();
        } catch (e) {
          console.warn('SIP: Error unregistering:', e);
        }
        this.registerer.dispose();
        this.registerer = null;
      }

      if (this.userAgent) {
        try {
          await this.userAgent.stop();
        } catch (e) {
          console.warn('SIP: Error stopping user agent:', e);
        }
        this.userAgent = null;
      }
    } finally {
      this.isDisconnecting = false;
    }
  }

  async makeCall(destination: string): Promise<string> {
    if (!this.userAgent) {
      throw new Error('User agent not initialized');
    }

    const target = UserAgent.makeURI(`sip:${destination}@${this.userAgent.configuration.uri?.host}`);
    if (!target) {
      throw new Error('Invalid destination');
    }

    const inviter = new Inviter(this.userAgent, target);
    const sessionId = this.generateSessionId();

    const callSession: CallSession = {
      id: sessionId,
      session: inviter,
      direction: 'outbound',
      remoteIdentity: destination,
      state: inviter.state,
    };

    this.sessions.set(sessionId, callSession);
    this.setupSessionHandlers(callSession);

    try {
      await inviter.invite();
      return sessionId;
    } catch (error) {
      this.sessions.delete(sessionId);
      throw error;
    }
  }

  async answer(sessionId: string): Promise<void> {
    const callSession = this.sessions.get(sessionId);
    if (!callSession || !(callSession.session instanceof Invitation)) {
      throw new Error('Invalid session or not an incoming call');
    }

    try {
      await callSession.session.accept({
        sessionDescriptionHandlerOptions: {
          constraints: { audio: true, video: false },
        },
      });
    } catch (error) {
      console.error('Failed to answer call:', error);
      throw error;
    }
  }

  async hangup(sessionId: string): Promise<void> {
    const callSession = this.sessions.get(sessionId);
    if (!callSession) {
      return;
    }

    try {
      switch (callSession.session.state) {
        case SessionState.Initial:
        case SessionState.Establishing:
          if (callSession.session instanceof Inviter) {
            await callSession.session.cancel();
          } else if (callSession.session instanceof Invitation) {
            await callSession.session.reject();
          }
          break;
        case SessionState.Established:
          await callSession.session.bye();
          break;
      }
    } catch (error) {
      console.error('Error hanging up call:', error);
    } finally {
      this.cleanupSession(sessionId);
    }
  }

  async sendDTMF(sessionId: string, tone: string): Promise<void> {
    const callSession = this.sessions.get(sessionId);
    if (!callSession || callSession.session.state !== SessionState.Established) {
      throw new Error('Session not established');
    }

    const sessionDescriptionHandler = callSession.session.sessionDescriptionHandler;
    if (sessionDescriptionHandler && 'sendDtmf' in sessionDescriptionHandler) {
      (sessionDescriptionHandler as any).sendDtmf(tone);
    }
  }

  async hold(sessionId: string): Promise<void> {
    const callSession = this.sessions.get(sessionId);
    if (!callSession || callSession.session.state !== SessionState.Established) {
      throw new Error('Session not established');
    }

    await (callSession.session as any).hold();
  }

  async unhold(sessionId: string): Promise<void> {
    const callSession = this.sessions.get(sessionId);
    if (!callSession || callSession.session.state !== SessionState.Established) {
      throw new Error('Session not established');
    }

    await (callSession.session as any).unhold();
  }

  getSession(sessionId: string): CallSession | undefined {
    return this.sessions.get(sessionId);
  }

  getAllSessions(): CallSession[] {
    return Array.from(this.sessions.values());
  }

  on(event: SIPEventType, handler: SIPEventHandler): () => void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(handler);

    return () => {
      this.eventHandlers.get(event)?.delete(handler);
    };
  }

  private emit(event: SIPEventType, data?: any): void {
    this.eventHandlers.get(event)?.forEach((handler) => handler(data));
  }

  private handleIncomingCall(invitation: Invitation): void {
    const sessionId = this.generateSessionId();
    const remoteIdentity = invitation.remoteIdentity.uri.user || 'Unknown';

    const callSession: CallSession = {
      id: sessionId,
      session: invitation,
      direction: 'inbound',
      remoteIdentity,
      state: invitation.state,
    };

    this.sessions.set(sessionId, callSession);
    this.setupSessionHandlers(callSession);

    // Play ring tone
    this.startRingTone();

    this.emit('incomingCall', {
      sessionId,
      from: remoteIdentity,
    });
  }

  private setupSessionHandlers(callSession: CallSession): void {
    const { session, id } = callSession;

    session.stateChange.addListener((state: SessionState) => {
      callSession.state = state;

      switch (state) {
        case SessionState.Established:
          this.stopRingTone();
          this.setupMediaStreams(callSession);
          this.emit('callEstablished', { sessionId: id });
          break;
        case SessionState.Terminated:
          this.stopRingTone();
          this.emit('callEnded', { sessionId: id });
          this.cleanupSession(id);
          break;
      }
    });
  }

  private setupMediaStreams(callSession: CallSession): void {
    const sessionDescriptionHandler = callSession.session.sessionDescriptionHandler;
    if (!sessionDescriptionHandler) return;

    const peerConnection = (sessionDescriptionHandler as any).peerConnection;
    if (!peerConnection) return;

    const remoteStream = new MediaStream();

    // Listen for incoming tracks (fires during SDP negotiation, before Established)
    peerConnection.addEventListener('track', (event: RTCTrackEvent) => {
      if (event.track) {
        remoteStream.addTrack(event.track);
      }
      if (this.audioElement) {
        this.audioElement.srcObject = remoteStream;
        this.audioElement.play().catch(() => {
          // Autoplay may be blocked; will play once user interacts
        });
      }
    });

    // Also drain any receivers already present (outbound calls may already have tracks)
    peerConnection.getReceivers().forEach((receiver: RTCRtpReceiver) => {
      if (receiver.track) {
        remoteStream.addTrack(receiver.track);
      }
    });

    callSession.remoteStream = remoteStream;

    if (this.audioElement && remoteStream.getTracks().length > 0) {
      this.audioElement.srcObject = remoteStream;
      this.audioElement.play().catch(() => {});
    }
  }

  private cleanupSession(sessionId: string): void {
    const callSession = this.sessions.get(sessionId);
    if (callSession) {
      if (callSession.localStream) {
        callSession.localStream.getTracks().forEach((track) => track.stop());
      }
      this.sessions.delete(sessionId);
    }
  }

  private generateSessionId(): string {
    return `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

export const sipClient = new SIPClient();
